<template>
    <ModalBase
        name="descargar-plazos"
        title="Descargar listado de plazos"
        @close="$emit('close', 'descargar-plazos')"
    >
        <template v-if="!descargaSolicitada">
            <div class="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:text-center">
                <p class="tw:text-base tw:font-semibold tw:text-gray-800">
                    Se generará un archivo Excel con los datos de los plazos
                    <template v-if="tieneFiltros">
                        aplicando los siguientes filtros:
                    </template>
                    <template v-else>
                        sin filtros aplicados.
                    </template>
                </p>

                <div v-if="tieneFiltros" class="tw:w-full tw:space-y-2">
                    <div
                        v-for="filtro in filtrosActivos"
                        :key="filtro.label"
                        class="tw:flex tw:justify-between tw:py-1 tw:px-3 tw:bg-gray-50 tw:rounded-lg"
                    >
                        <span class="tw:text-sm tw:font-semibold tw:text-gray-600">{{ filtro.label }}:</span>
                        <span class="tw:text-sm tw:text-gray-800">{{ filtro.value }}</span>
                    </div>
                </div>
            </div>
        </template>

        <template v-else>
            <div class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-4">
                <p class="tw:text-base tw:font-semibold tw:text-gray-800">
                    Se está generando el informe, recibirá una notificación para poder descargarlo cuando esté listo.
                </p>
            </div>
        </template>

        <template #footer>
            <div class="tw:flex tw:gap-3 tw:justify-end">
                <Button
                    variant="ghost"
                    :disabled="loading"
                    @click="$emit('close', 'descargar-plazos')"
                >
                    Cancelar
                </Button>
                <Button
                    v-if="!descargaSolicitada"
                    :loading="loading"
                    :disabled="loading"
                    @click="descargarExcel"
                >
                    DESCARGAR
                </Button>
            </div>
        </template>
    </ModalBase>
</template>

<script setup>
    import { computed, ref } from 'vue'
    import store from '@/store'
    import { config } from '@/config.js'
    import { useToast } from '@/composables/common/useToast'
    import ModalBase from '@/modules/admin/ui/ModalBase.vue'
    import Button from '@/modules/admin/ui/Button.vue'

    const ESTADO_MAP = {
        0: 'Pendiente',
        1: 'Pagado',
        2: 'Anulado',
        3: 'Fallido',
        4: 'Fallido Definitivo',
        5: 'Bloqueado'
    }

    const props = defineProps({
        filters: { type: Object, default: () => ({}) },
        tipo: { type: String, default: 'abono' },
        lista_id: { type: [Number, String], required: true },
        equipos: { type: Array, default: () => [] }
    })

    const emit = defineEmits(['close'])
    const toast = useToast()

    const loading = ref(false)
    const descargaSolicitada = ref(false)

    const tieneFiltros = computed(() => filtrosActivos.value.length > 0)

    const filtrosActivos = computed(() => {
        const items = []
        const f = props.filters || {}

        if (f.search) {
            items.push({ label: 'Búsqueda', value: f.search })
        }
        if (f.estado !== '' && f.estado !== null && f.estado !== undefined) {
            const estadoLabel = ESTADO_MAP[f.estado] || f.estado
            items.push({ label: 'Estado', value: estadoLabel })
        }
        if (f.fecha_desde) {
            items.push({ label: 'Fecha desde', value: f.fecha_desde })
        }
        if (f.fecha_hasta) {
            items.push({ label: 'Fecha hasta', value: f.fecha_hasta })
        }
        if (f.equipo && f.equipo.length) {
            const nombres = f.equipo.map(id => {
                const eq = props.equipos.find(e => e.id == id)
                return eq ? eq.nombre : id
            })
            items.push({ label: 'Equipo', value: nombres.join(', ') })
        }

        return items
    })

    async function descargarExcel() {
        loading.value = true

        const { search, estado, fecha_desde, fecha_hasta, equipo } = props.filters || {}
        const params = {
            filters: { search, estado, fecha_desde, fecha_hasta, equipo, tipo: props.tipo },
            lista_id: props.lista_id
        }

        try {
            await store.dispatch(
                config.getVuexAlias('back.lista.plazos.descargar-excel'),
                params
            )
            descargaSolicitada.value = true
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al descargar el listado')
        } finally {
            loading.value = false
        }
    }
</script>
